import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart, BarChartTickXFormats, TickX, TickY } from '@charts';
import { Box } from '@base-app';
import { ConsentTrendProps } from './ConsentTrend.types';
import { CardGrid, Direction, NoData } from '@builder';
import { formatStackedBarData, formatStackedBarInfoLegend } from '@modules/study-insights/utils';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { StyledConsentTrendContainer } from './ConsentTrend.styles';

import SamplesSequencedInfo from './SamplesSequencedInfo/SamplesSequencedInfo';
import { COMMON_TRANSLATION } from '@/common/constants';

const ConsentTrend: FC<ConsentTrendProps> = ({ data }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  const legend = formatStackedBarInfoLegend(data);
  const tickY: TickY = { max: 3000, min: 0, unit: { symbol: 'K' }, scale: 1000 };
  const tickX: TickX = { transformLabelFn: BarChartTickXFormats.label.labelSplitBySpaces };

  const formattedData = useMemo(() => {
    return formatStackedBarData(data.metric);
  }, [data]);

  return (
    <StyledConsentTrendContainer>
      <CardGrid
        css={{ width: '100%' }}
        direction={Direction.ROW}
        infoPanel={
          <SamplesSequencedInfo
            legendData={legend}
            title={t('samples_sequenced.total_samples_sequenced').toLocaleUpperCase()}
            total={data.metric.total}
          />
        }
        title={t('samples_sequenced.total_samples_sequenced')}
      >
        <Box>
          {formattedData?.length ? (
            <BarChart
              data={formattedData}
              tickX={tickX}
              tickY={tickY}
              startAtEnd={true}
              tooltip={t(`samples_sequenced.samples_sequenced`)}
            />
          ) : (
            <NoData message={t('no_data_available', { ns: COMMON_TRANSLATION })} />
          )}
        </Box>
      </CardGrid>
    </StyledConsentTrendContainer>
  );
};

export default ConsentTrend;
